/**
 * The shared nodes, on a canvas — read-only.
 *
 * A canvas share carries the arrangement the author built: where each node sits
 * and what connects to what. This draws exactly that, and only the shared nodes:
 * cards at their positions, connected by the same curved wires the app uses, on
 * a plane you can pan and zoom but not edit. It opens fitted to the whole graph,
 * so the shape is legible before you touch anything.
 *
 * Answers are markdown, and any pictures they name travel in the share's `files`
 * map by the same path — so before a card is rendered its image paths are
 * rewritten to the data URLs the share carried.
 */
import * as React from 'react'
import { Maximize2, Minus, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { AirAnswer, AirNodeCard, UserMessageBubble, openExternalUrl, route } from '@synti/ui'
import type { Point } from '@synti/ui'

/**
 * A card's rectangle. Wider than the shared `Box`, which is the vertical extent
 * `route` needs to clear a card; the sides are this file's own business — where
 * a port sits on the edge of a card.
 */
interface CardBox {
  top: number
  bottom: number
  left: number
  right: number
}
import type { AirShareCanvas, SharedCanvasNode, SharedFile } from '@synti/core'

/** A node's default width, matching a card on the canvas it came from. */
const DEFAULT_W = 560
/** A height to assume for a node until its card has been measured. */
const EST_H = 240
const openUrl = (url: string) => {
  const result = openExternalUrl(url)
  if (!result.opened) console.warn('[viewer:canvas] blocked URL:', url)
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))

/**
 * Rewrite the paths an answer names to the data URLs the share carried, so a
 * picture drawn on the canvas shows here too. A share that left an image behind
 * simply has no entry, and the path stays as it was — a broken image beats a
 * silent gap.
 */
function resolveImages(md: string | undefined, files: Record<string, SharedFile>): string {
  if (!md) return ''
  let out = md
  for (const [path, f] of Object.entries(files)) {
    if (!out.includes(path)) continue
    out = out.split(path).join(`data:${f.mime};base64,${f.data}`)
  }
  return out
}

export function AirCanvasView({
  canvas,
  files,
  answers,
}: {
  canvas: AirShareCanvas
  files: Record<string, SharedFile>
  /**
   * The shared conversation's answers, in order, as a last resort.
   *
   * A node carries the text its card was showing when it was shared. Some do
   * not — an older app wrote none, and a share built by anything but the
   * canvas button has nowhere to get it — and the card then drew a dash beside
   * a Transcript tab holding the whole exchange. The reader can see the app
   * had the answer; so should the card.
   */
  answers?: string[]
}) {
  const { t } = useTranslation()
  const nodes = canvas.nodes ?? []
  /**
   * Matched by position, and only when every node can be matched.
   *
   * A conversation share is its blocks laid end to end, so the nth answer
   * belongs to the nth card. That holds exactly while the counts agree — a
   * partial share, or a transcript with an extra turn in it, would shift every
   * card onto its neighbour's answer, and a dash is far better than the wrong
   * text under the right question.
   */
  const fallback = React.useMemo(() => {
    if (!answers || answers.length !== nodes.length) return null
    return new Map(nodes.map((n, i) => [n.id, answers[i] ?? '']))
  }, [answers, nodes])
  const stageRef = React.useRef<HTMLDivElement>(null)
  const [heights, setHeights] = React.useState<Record<string, number>>({})
  const [view, setView] = React.useState({ x: 0, y: 0, scale: 1 })
  const [panning, setPanning] = React.useState(false)
  const fittedRef = React.useRef(false)

  const minX = nodes.length ? Math.min(...nodes.map((n) => n.x)) : 0
  const minY = nodes.length ? Math.min(...nodes.map((n) => n.y)) : 0
  const px = React.useCallback((n: SharedCanvasNode) => n.x - minX, [minX])
  const py = React.useCallback((n: SharedCanvasNode) => n.y - minY, [minY])
  const widthOf = (n: SharedCanvasNode) => n.width ?? DEFAULT_W
  const heightOf = React.useCallback(
    (n: SharedCanvasNode) => heights[n.id] ?? n.height ?? EST_H,
    [heights],
  )

  // Each card reports its measured height, which the wires need for their
  // endpoints and the fit needs for the content bounds.
  const measure = React.useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (!el) return
      const h = el.offsetHeight
      setHeights((prev) => (prev[id] === h ? prev : { ...prev, [id]: h }))
    },
    [],
  )

  // Fit the whole graph into view once, after every card has been measured.
  // Only once — a later measurement must not yank the plane back from where the
  // reader has panned it.
  React.useLayoutEffect(() => {
    if (fittedRef.current) return
    const stage = stageRef.current
    if (!stage || nodes.length === 0) return
    if (nodes.some((n) => heights[n.id] === undefined)) return
    const contentW = Math.max(...nodes.map((n) => px(n) + widthOf(n)))
    const contentH = Math.max(...nodes.map((n) => py(n) + heightOf(n)))
    const vw = stage.clientWidth
    const vh = stage.clientHeight
    const pad = 48
    const scale = clamp(Math.min((vw - pad * 2) / contentW, (vh - pad * 2) / contentH), 0.2, 1)
    setView({
      x: (vw - contentW * scale) / 2,
      y: Math.max(pad, (vh - contentH * scale) / 2),
      scale,
    })
    fittedRef.current = true
  }, [heights, nodes, px, py, heightOf])

  const startPan = (e: React.PointerEvent) => {
    // A press on a card is for reading it — selecting text, following a link.
    // Only the empty plane pans.
    if ((e.target as HTMLElement).closest('[data-node]')) return
    const startX = e.clientX
    const startY = e.clientY
    const start = view
    setPanning(true)
    const move = (ev: PointerEvent) =>
      setView({ ...start, x: start.x + (ev.clientX - startX), y: start.y + (ev.clientY - startY) })
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setPanning(false)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const rect = stageRef.current?.getBoundingClientRect()
    const cx = e.clientX - (rect?.left ?? 0)
    const cy = e.clientY - (rect?.top ?? 0)
    setView((v) => {
      const scale = clamp(v.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1), 0.2, 2)
      const k = scale / v.scale
      return { scale, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k }
    })
  }

  const zoomBy = (factor: number) =>
    setView((v) => {
      const stage = stageRef.current
      const cx = (stage?.clientWidth ?? 0) / 2
      const cy = (stage?.clientHeight ?? 0) / 2
      const scale = clamp(v.scale * factor, 0.2, 2)
      const k = scale / v.scale
      return { scale, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k }
    })

  const fit = () => {
    fittedRef.current = false
    setHeights((h) => ({ ...h })) // nudge the fit effect to run again
  }

  if (nodes.length === 0) return null

  const contentW = Math.max(...nodes.map((n) => px(n) + widthOf(n)))
  const contentH = Math.max(...nodes.map((n) => py(n) + heightOf(n)))
  const byId = new Map(nodes.map((n) => [n.id, n]))

  return (
    <div
      ref={stageRef}
      onPointerDown={startPan}
      onWheel={onWheel}
      className="relative min-h-0 flex-1 overflow-hidden bg-foreground-2"
      style={{ cursor: panning ? 'grabbing' : 'grab' }}
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          opacity: fittedRef.current ? 1 : 0,
          transition: 'opacity 150ms ease',
        }}
      >
        {/* The wires, under the cards. Scales with the plane; never intercepts a
            pointer, so a drag over a wire still pans. */}
        <svg
          className="pointer-events-none absolute left-0 top-0 overflow-visible"
          width={contentW}
          height={contentH}
        >
          <defs>
            <marker id="viewer-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M 0 0 L 8 4 L 0 8 z" className="fill-foreground/30" />
            </marker>
          </defs>
          {canvas.edges?.map((e, i) => {
            const f = byId.get(e.from)
            const tNode = byId.get(e.to)
            if (!f || !tNode) return null
            const fBox: CardBox = {
              left: px(f),
              right: px(f) + widthOf(f),
              top: py(f),
              bottom: py(f) + heightOf(f),
            }
            const tBox: CardBox = {
              left: px(tNode),
              right: px(tNode) + widthOf(tNode),
              top: py(tNode),
              bottom: py(tNode) + heightOf(tNode),
            }
            const from: Point = { x: fBox.right, y: (fBox.top + fBox.bottom) / 2 }
            const to: Point = { x: tBox.left, y: (tBox.top + tBox.bottom) / 2 }
            return (
              <path
                key={`${e.from}->${e.to}-${i}`}
                d={route(from, to, fBox, tBox)}
                fill="none"
                className="stroke-foreground/25"
                strokeWidth={1.5}
                markerEnd="url(#viewer-arrow)"
              />
            )
          })}
        </svg>

        {nodes.map((n) => (
          // The same card the app draws on its canvas — chrome, star, name pill
          // and headline cost from one component, so a share cannot drift out of
          // step with the thing it is a picture of. Only the body differs: here
          // it is the shared answer in full.
          <div
            key={n.id}
            ref={measure(n.id)}
            data-node
            className="absolute"
            style={{ left: px(n), top: py(n) }}
          >
            <AirNodeCard {...(n.number ? { title: n.number } : {})} width={widthOf(n)}>
              {/* The question above the answer, as the share format's own type
                  says it sits "on the card" — and in the same bubble the app
                  draws it in, rather than as small grey text in a header. */}
              {n.prompt?.trim() && (
                <UserMessageBubble content={n.prompt} onUrlClick={openUrl} />
              )}
              {/* The app's own answer component, not a second rendering of the
                  same thing: full markdown (tables, code blocks, the typography a
                  code review needs) and the same click-to-enlarge on pictures.
                  Files are inert here — a reader has no disk of ours. */}
              <div className="text-[14px] leading-relaxed">
                {(n.output || fallback?.get(n.id)) ? (
                  <AirAnswer text={resolveImages(n.output || fallback?.get(n.id), files)} onUrlClick={openUrl} />
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
            </AirNodeCard>
          </div>
        ))}
      </div>

      {/* Zoom, and a way back to the whole picture. */}
      <div className="absolute bottom-4 right-4 flex items-center gap-1 rounded-lg border border-border bg-background/90 p-1 shadow-middle backdrop-blur">
        <button
          type="button"
          onClick={() => zoomBy(1 / 1.2)}
          aria-label={t('viewer.zoomOut', { defaultValue: 'Zoom out' })}
          className="rounded-md p-1.5 text-foreground/60 transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
        >
          <Minus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={fit}
          aria-label={t('viewer.fit', { defaultValue: 'Fit to screen' })}
          className="rounded-md p-1.5 text-foreground/60 transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => zoomBy(1.2)}
          aria-label={t('viewer.zoomIn', { defaultValue: 'Zoom in' })}
          className="rounded-md p-1.5 text-foreground/60 transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
